import { useMemo, useRef, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Combobox } from "@/components/common/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Loader2, ShieldCheck, CheckCircle2, AlertTriangle,
  ShieldAlert, Plus, UserPlus, ExternalLink, Database, Lock, Boxes,
} from "lucide-react";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject; r.readAsDataURL(file);
  });
}

const UNIT_MAP = {
  kg: "kg", kgs: "kg", "k.g.": "kg", kilogram: "kg", kilograms: "kg",
  g: "g", gm: "g", gms: "g", gram: "g", grams: "g",
  pkt: "packet", pack: "packet", packet: "packet", packets: "packet",
  litre: "litre", ltr: "litre", litres: "litre", liter: "litre", liters: "litre", l: "litre",
  ml: "ml", pcs: "pcs", pc: "pcs", piece: "pcs", pieces: "pcs", nos: "pcs", no: "pcs",
};
const normalizeUnit = (u) => UNIT_MAP[String(u || "").trim().toLowerCase()] || (u || "");
const normName = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[.,]/g, "");
const normGst = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, "");

// Read-only cell for locked historical values.
const RO = ({ children, testId, className = "" }) => (
  <span data-testid={testId} className={`font-mono text-xs text-stone-700 ${className}`}>{children}</span>
);

export default function SupplierBillUpload() {
  const inputRef = useRef(null);
  const { role } = useAuth();
  const canReceiveStock = ["super_admin", "stores_manager", "store_executive", "purchase_manager", "management"].includes(role);
  const { data: supData, refetch: refetchSup } = useResource("/suppliers");
  const { data: ingData, refetch: refetchIng } = useResource("/ingredients");
  const { data: pbData, refetch: refetchBills } = useResource("/purchase-bills");
  const suppliers = useMemo(() => (Array.isArray(supData) ? supData : []).filter((s) => s.active !== false), [supData]);
  const ingredients = useMemo(() => (Array.isArray(ingData) ? ingData : []), [ingData]);
  const pendingBills = useMemo(() => (Array.isArray(pbData) ? pbData : []).filter((b) => (b.status || "").toUpperCase().includes("PENDING")), [pbData]);

  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [bill, setBill] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [supplierMatchBasis, setSupplierMatchBasis] = useState("");
  const [ambiguous, setAmbiguous] = useState([]);
  const [lines, setLines] = useState([]);

  const [supDlg, setSupDlg] = useState(false);
  const [supForm, setSupForm] = useState({});
  const [supDup, setSupDup] = useState(null);
  const [ingDlg, setIngDlg] = useState(null); // line index
  const [ingForm, setIngForm] = useState({});
  const [ingDup, setIngDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);

  const billId = bill?.id || bill?.billId;

  const prefill = (r) => {
    setBill(r);
    // Supplier auto-match: backend match -> GSTIN exact -> name (ambiguity requires manual pick).
    let sid = r.supplierId || "";
    let basis = r.supplierId ? "backend match" : "";
    let amb = [];
    const supList = Array.isArray(supData) ? supData : [];
    if (!sid && r.supplierGstin) {
      const g = supList.find((s) => s.gstin && normGst(s.gstin) === normGst(r.supplierGstin));
      if (g) { sid = g.id; basis = "GSTIN"; }
    }
    if (!sid && r.supplierName) {
      const matches = supList.filter((s) => normName(s.name) === normName(r.supplierName));
      if (matches.length === 1) { sid = matches[0].id; basis = "name"; }
      else if (matches.length > 1) { amb = matches; }
    }
    setSupplierId(sid);
    setSupplierMatchBasis(basis);
    setAmbiguous(amb);
    setSupForm({
      name: r.supplierName || "", gstin: r.supplierGstin || "", pan: r.supplierPan || "",
      address: r.supplierAddress || "", phone: r.supplierContact || r.supplierPhone || "",
      email: r.supplierEmail || "", city: "", state: "", pin: "", contactPerson: "",
    });
    const rl = r.lines || r.items || [];
    setLines((Array.isArray(rl) ? rl : []).map((l, i) => ({
      lineNo: l.lineNo ?? i + 1,
      description: l.description || l.ingredientName || l.name || "",
      supplierItemCode: l.supplierItemCode || l.ingredientCode || "",
      ingredientId: l.ingredientId || "",
      suggestedIngredientId: l.suggestedIngredientId || "",
      quantity: l.quantity ?? l.qty ?? "",
      invoiceUnit: l.invoiceUnit || l.unit || "",
      unit: normalizeUnit(l.unit || l.invoiceUnit || ""),
      packSize: l.packSize ?? 0,
      rate: l.rate ?? l.invoiceUnitRate ?? "",
      gstPct: l.gstPct ?? l.taxPct ?? "",
      gstAmount: l.gstAmount ?? "",
      taxableValue: l.taxableValue ?? "",
      total: l.total ?? "",
      status: l.status || (l.ingredientId ? "MATCHED" : "PENDING"),
    })));
  };

  const loadExisting = async (id) => {
    try {
      const res = await api.get(`/purchase-bills/${id}`);
      prefill(res.data);
      toast.success("Bill loaded. Review matches, then Import master data. No stock is created.");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not load bill")); }
  };

  const onExtract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const b64 = await toBase64(file);
      const res = await api.post("/purchase-bills/extract", { filename: file.name, mimeType: file.type, contentBase64: b64, storeDocument: true });
      prefill(res.data);
      refetchBills();
      toast.success("Bill read. Review matches, then Import master data. No stock is created.");
    } catch (e) { toast.error(apiErrorMessage(e, "Extraction failed")); }
    finally { setExtracting(false); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const [editingMatch, setEditingMatch] = useState({});

  const unmatchLine = (i) => setLine(i, { ingredientId: "", status: "PENDING" });

  const matchLine = (i, ingredientId) => {
    if (!ingredientId) return;
    if (lines.some((l, idx) => idx !== i && l.ingredientId === ingredientId)) {
      toast.error("That ingredient is already matched on another line."); return;
    }
    setLine(i, { ingredientId, status: "MATCHED" });
    setEditingMatch((s) => ({ ...s, [i]: false }));
  };

  // ---- Add New Supplier (explicit) with duplicate protection ----
  const openSupplierDialog = () => { setSupDup(null); setSupDlg(true); };
  const checkSupplierDuplicate = () => {
    const byGst = supForm.gstin ? suppliers.find((s) => s.gstin && normGst(s.gstin) === normGst(supForm.gstin)) : null;
    const byName = suppliers.find((s) => normName(s.name) === normName(supForm.name));
    return byGst || byName || null;
  };
  const createSupplier = async () => {
    const dup = checkSupplierDuplicate();
    if (dup && !supDup) { setSupDup(dup); return; } // first click surfaces the duplicate warning
    setBusy(true);
    try {
      const res = await api.post(`/purchase-bills/${billId}/create-supplier`, {
        name: supForm.name, gstin: supForm.gstin, pan: supForm.pan, address: supForm.address,
        phone: supForm.phone, email: supForm.email, city: supForm.city, state: supForm.state,
        pin: supForm.pin, contactPerson: supForm.contactPerson, approved: false,
      });
      const sid = res.data?.supplierId || res.data?.id;
      const code = res.data?.supplierCode || res.data?.supplierId;
      toast.success(`Supplier created${code ? " (" + code + ")" : ""}`);
      setSupDlg(false); setSupDup(null); await refetchSup();
      if (sid) { setSupplierId(sid); setSupplierMatchBasis("created"); setAmbiguous([]); }
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create supplier")); }
    finally { setBusy(false); }
  };

  // ---- Add New Ingredient (explicit) with duplicate protection ----
  const openIngredientDialog = (i) => {
    const l = lines[i];
    setIngForm({ name: l.description, brand: "", unit: l.unit || "pcs", packingSize: l.packSize || "", packingUnit: l.unit || "", category: "" });
    setIngDup(null); setIngDlg(i);
  };
  const checkIngredientDuplicate = () => ingredients.find((x) => normName(x.name) === normName(ingForm.name)) || null;
  const createIngredient = async () => {
    const dup = checkIngredientDuplicate();
    if (dup && !ingDup) { setIngDup(dup); return; }
    setBusy(true);
    try {
      const res = await api.post("/ingredients", {
        name: ingForm.name, unit: ingForm.unit, consumptionUnit: ingForm.unit,
        brand: ingForm.brand || "", packingSize: Number(ingForm.packingSize) || 0,
        packingUnit: ingForm.packingUnit || ingForm.unit || "", category: ingForm.category || "",
        rate: 0, source: "HISTORICAL_MASTER_DATA_IMPORT", active: true,
      });
      const newId = res.data?.id;
      await refetchIng();
      if (ingDlg != null && newId) setLine(ingDlg, { ingredientId: newId, status: "NEW_CREATED", unit: ingForm.unit });
      toast.success(`Ingredient "${ingForm.name}" created${res.data?.ingredientId ? " (" + res.data.ingredientId + ")" : ""}`);
      setIngDlg(null); setIngDup(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create ingredient")); }
    finally { setBusy(false); }
  };

  // ---- Import master data + price history (NO stock / GRN / AP) ----
  const importMasterData = async () => {
    setConfirmOpen(false); setBusy(true);
    try {
      if (supplierId) await api.post(`/purchase-bills/${billId}/match-supplier`, { supplierId });
      for (const l of lines) {
        if (!l.ingredientId) continue;
        await api.post(`/purchase-bills/${billId}/lines/${l.lineNo}/match`, { ingredientId: l.ingredientId, saveCodeMap: true });
        await api.post(`/purchase-bills/${billId}/lines/${l.lineNo}/apply-price`, {}).catch(() => {});
      }
      await api.post(`/purchase-bills/${billId}/mark-imported`, {});
      toast.success("Master data & price history imported. No stock / GRN / purchase transaction created.");
      setBill(null); setFile(null); setLines([]); setSupplierId("");
      refetchBills();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  // ---- Save masters/prices then receive billed quantities directly into RM stock ----
  const receiveToStock = async () => {
    // Bulk "Match All + Receive" must not silently skip lines — block on any unresolved line.
    const unresolved = lines.filter((l) => l.description && !l.ingredientId);
    if (unresolved.length) {
      setReceiveOpen(false);
      toast.error(`${unresolved.length} line(s) are not matched. Match or add every item before receiving.`);
      return;
    }
    if (!supplierId) { setReceiveOpen(false); return toast.error("Match or create the supplier first."); }
    setReceiveOpen(false); setBusy(true);
    try {
      await api.post(`/purchase-bills/${billId}/match-supplier`, { supplierId });
      for (const l of lines) {
        if (!l.ingredientId) continue;
        await api.post(`/purchase-bills/${billId}/lines/${l.lineNo}/match`, { ingredientId: l.ingredientId, saveCodeMap: true });
        await api.post(`/purchase-bills/${billId}/lines/${l.lineNo}/apply-price`, {}).catch(() => {});
      }
      const res = await api.post(`/purchase-bills/${billId}/receive-stock`, {});
      toast.success(`Stock received: ${res.data?.linesReceived ?? 0} item(s) added to raw-material inventory.`);
      setBill(null); setFile(null); setLines([]); setSupplierId("");
      refetchBills(); refetchIng();
    } catch (e) { toast.error(apiErrorMessage(e, "Receive to stock failed")); }
    finally { setBusy(false); }
  };

  const allResolved = lines.length > 0 && lines.every((l) => !l.description || l.ingredientId);

  const matchedSupplier = suppliers.find((s) => s.id === supplierId);
  const supplierCode = matchedSupplier?.supplierId || matchedSupplier?.code || "";
  const newCount = lines.filter((l) => !l.ingredientId && l.description).length;
  const matchedCount = lines.filter((l) => l.ingredientId).length;
  const docUrl = bill?.document?.url || bill?.documentUrl;
  const billTotal = bill?.grandTotal ?? bill?.total ?? bill?.billTotal ?? null;
  const canImport = !!billId && !!supplierId && matchedCount > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Supplier Bill — Scan &amp; Receive" subtitle="Scan a supplier bill (PDF/JPG/PNG), match or create Supplier &amp; Ingredient masters, then either import master data only or receive the billed quantities directly into raw-material stock." />

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span><strong>Two ways to finish.</strong> After matching the Supplier &amp; Ingredients you can either <strong>Import Master Data (no stock)</strong> — builds masters &amp; price history only — or <strong>Receive to Stock</strong>, which additionally adds the billed quantities straight into raw-material inventory (ledger-backed, skips GRN/QA). Extracted quantities &amp; prices are the original bill values and are read-only.</span>
      </div>

      {/* Upload */}
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div data-testid="supbill-dropzone" onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 py-10 text-center hover:border-amber-400 hover:bg-amber-50/40">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><UploadCloud className="h-6 w-6" /></div>
          {file ? <div className="flex items-center gap-2 text-sm font-medium text-stone-800"><FileText className="h-4 w-4 text-amber-700" /> {file.name}</div>
            : <><div className="font-display text-base font-semibold text-stone-800">Select a supplier bill</div><div className="mt-1 text-xs text-muted-foreground">PDF, JPG, JPEG, PNG</div></>}
          <input ref={inputRef} data-testid="supbill-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="supbill-extract" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={!file || extracting} onClick={onExtract}>
            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Extract &amp; Read
          </Button>
        </div>
      </div>

      {!bill && pendingBills.length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="supbill-pending-list">
          <h3 className="mb-2 font-display text-sm font-semibold text-stone-900">Pending supplier bills ({pendingBills.length})</h3>
          <div className="divide-y divide-stone-100">
            {pendingBills.map((b) => (
              <button key={b.id} data-testid={`supbill-pending-${b.id}`} onClick={() => loadExisting(b.id)}
                className="flex w-full items-center justify-between px-1 py-2 text-left text-sm hover:bg-amber-50/40">
                <span className="font-medium text-stone-800">{b.supplierName || "Unknown supplier"}</span>
                <span className="text-xs text-stone-500">Inv {b.supplierInvoiceNumber || "—"} · {b.invoiceDate || "—"} · {(b.lines || []).length} items</span>
                <span className="text-xs font-medium text-amber-700">Review →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {bill && (
        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-stone-900">Review Extraction</h3>
            {docUrl && <a data-testid="supbill-view-doc" href={docUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-amber-700 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View original bill</a>}
          </div>

          {/* SUPPLIER */}
          <div className="rounded-lg border border-stone-200 p-4">
            <Label className="text-xs font-bold uppercase tracking-wide text-stone-500">Supplier</Label>
            {matchedSupplier ? (
              <div className="mt-1.5" data-testid="supbill-supplier-matched">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {matchedSupplier.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-4 text-xs text-stone-500">
                  <span>Supplier ID: <strong className="text-stone-700" data-testid="supbill-supplier-id">{supplierCode}</strong></span>
                  {matchedSupplier.gstin && <span>GSTIN: <strong className="text-stone-700">{matchedSupplier.gstin}</strong></span>}
                  {supplierMatchBasis && <span className="text-stone-400">matched by {supplierMatchBasis}</span>}
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {ambiguous.length > 1 ? (
                  <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="supbill-supplier-ambiguous"><AlertTriangle className="h-3.5 w-3.5" /> Multiple possible suppliers found — select the correct one.</div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-rose-600" data-testid="supbill-supplier-notfound"><AlertTriangle className="h-3.5 w-3.5" /> Supplier not found{bill.supplierName ? `: "${bill.supplierName}"` : ""}</div>
                )}
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Combobox testId="supbill-supplier-search" className="sm:w-96" value={supplierId} onChange={(v) => { setSupplierId(v); setSupplierMatchBasis("manual"); }} placeholder="Select existing supplier…"
                    options={suppliers.map((s) => ({ value: s.id, label: s.name, hint: s.supplierId || s.gstin }))} />
                  <Button data-testid="supbill-supplier-create" variant="outline" className="gap-1.5" onClick={openSupplierDialog}><UserPlus className="h-4 w-4" /> Add New Supplier</Button>
                </div>
              </div>
            )}
          </div>

          {/* INVOICE META (read-only) */}
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-stone-200 bg-stone-50/50 p-4 sm:grid-cols-4">
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Invoice No</div><RO testId="supbill-invno" className="text-sm">{bill.supplierInvoiceNumber || "—"}</RO></div>
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Invoice Date</div><RO testId="supbill-invdate" className="text-sm">{bill.invoiceDate || "—"}</RO></div>
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Supplier GSTIN</div><RO className="text-sm">{bill.supplierGstin || "—"}</RO></div>
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Bill Total</div><RO testId="supbill-total" className="text-sm font-semibold">{billTotal != null ? fmtCurrency(billTotal) : "—"}</RO></div>
          </div>

          {/* ITEMS — values read-only, only matching is actionable */}
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <div className="flex items-center gap-1.5 border-b border-stone-200 bg-stone-50 px-3 py-2 text-[11px] text-stone-500"><Lock className="h-3 w-3" /> Extracted quantities &amp; prices are the original historical bill values (read-only).</div>
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-3 py-2">Extracted Description</th><th className="px-3 py-2">Matched Item</th>
                  <th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2 text-right">Bill Rate</th><th className="px-3 py-2 text-right">Tax</th>
                  <th className="px-3 py-2 text-right">Taxable</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {lines.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-xs text-muted-foreground">No line items were extracted from this bill.</td></tr> :
                  lines.map((l, i) => {
                    const ing = ingredients.find((x) => x.id === l.ingredientId);
                    const sugIng = !l.ingredientId && l.suggestedIngredientId ? ingredients.find((x) => x.id === l.suggestedIngredientId) : null;
                    const matched = !!l.ingredientId;
                    return (
                      <tr key={i} data-testid={`supbill-line-${i}`}>
                        <td className="px-3 py-2"><div className="text-stone-800">{l.description || "—"}</div>{l.supplierItemCode && <div className="text-[10px] text-stone-400">code {l.supplierItemCode}</div>}</td>
                        <td className="px-3 py-2">
                          {matched && !editingMatch[i] ? (
                            <div data-testid={`supbill-line-matched-${i}`}>
                              <span className="text-xs font-medium text-emerald-700">{ing?.name || "matched"}</span>
                              {ing?.ingredientId && <div className="text-[10px] text-stone-400">{ing.ingredientId}</div>}
                              <div className="mt-0.5 flex items-center gap-2">
                                <button data-testid={`supbill-line-change-${i}`} className="text-[11px] text-amber-700 hover:underline" onClick={() => setEditingMatch((s) => ({ ...s, [i]: true }))}>Change</button>
                                <button data-testid={`supbill-line-unmatch-${i}`} className="text-[11px] text-rose-600 hover:underline" onClick={() => unmatchLine(i)}>✕ Reject match</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <Combobox testId={`supbill-line-ing-${i}`} value={l.ingredientId || ""} onChange={(v) => matchLine(i, v)} placeholder="Select existing ingredient…"
                                options={ingredients.map((x) => ({ value: x.id, label: x.name, hint: x.ingredientId || x.consumptionUnit || x.unit }))} />
                              {sugIng && (
                                <button type="button" data-testid={`supbill-line-suggest-${i}`} className="text-left text-[11px] text-amber-700 hover:underline" onClick={() => matchLine(i, sugIng.id)}>
                                  Did you mean <strong>{sugIng.name}</strong>? — click to match
                                </button>
                              )}
                              <div className="flex items-center gap-3">
                                <button data-testid={`supbill-line-create-${i}`} className="flex items-center gap-1 text-[11px] text-amber-700 hover:underline" onClick={() => openIngredientDialog(i)}>
                                  <Plus className="h-3 w-3" /> Add New Ingredient
                                </button>
                                {matched && (
                                  <button data-testid={`supbill-line-cancel-${i}`} className="text-[11px] text-stone-500 hover:underline" onClick={() => setEditingMatch((s) => ({ ...s, [i]: false }))}>Keep current match</button>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right"><RO testId={`supbill-qty-${i}`}>{l.quantity !== "" && l.quantity != null ? fmtNumber(l.quantity, 3) : "—"}</RO></td>
                        <td className="px-3 py-2"><RO>{l.invoiceUnit || l.unit || "—"}</RO></td>
                        <td className="px-3 py-2 text-right"><RO testId={`supbill-rate-${i}`}>{l.rate !== "" && l.rate != null ? fmtCurrency(l.rate) : "—"}</RO></td>
                        <td className="px-3 py-2 text-right"><RO>{l.gstPct !== "" && l.gstPct != null ? `${l.gstPct}%` : "—"}</RO></td>
                        <td className="px-3 py-2 text-right"><RO>{l.taxableValue !== "" && l.taxableValue != null ? fmtCurrency(l.taxableValue) : "—"}</RO></td>
                        <td className="px-3 py-2 text-right"><RO>{l.total !== "" && l.total != null ? fmtCurrency(l.total) : "—"}</RO></td>
                        <td className="px-3 py-2 text-[11px]">
                          {matched ? <span className="text-emerald-600" data-testid={`supbill-status-${i}`}>{l.status === "NEW_CREATED" ? "✓ New — created" : "✓ Matched"}</span>
                            : <span className="text-rose-600" data-testid={`supbill-status-${i}`}>Not matched</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {newCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4" /> {newCount} item(s) not matched. Match to an existing ingredient or use <strong>Add New Ingredient</strong> per line before importing.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-stone-500">{matchedCount}/{lines.length} items matched</span>
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="supbill-confirm" variant="outline" className="gap-2" disabled={busy || !canImport} onClick={() => setConfirmOpen(true)}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Import Master Data (no stock)
              </Button>
              {canReceiveStock && (
                <Button data-testid="supbill-receive" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !canImport || !allResolved || bill?.stockReceived} onClick={() => setReceiveOpen(true)}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Boxes className="h-4 w-4" />} {bill?.stockReceived ? "Stock Received" : `Match All + Receive${!allResolved ? " (resolve all lines)" : ""}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create supplier dialog */}
      <Dialog open={supDlg} onOpenChange={(o) => { setSupDlg(o); if (!o) setSupDup(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Add New Supplier</DialogTitle>
            <DialogDescription className="text-xs">Pre-filled from the bill. A permanent Supplier ID is generated on save. Source: Historical Master Data Import. No transaction is posted.</DialogDescription></DialogHeader>
          {supDup && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="supcreate-dup">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Possible duplicate: <strong>{supDup.name}</strong> ({supDup.supplierId || supDup.gstin}). Click “Create anyway” to proceed, or Cancel and select the existing supplier.
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1"><Label>Supplier name <span className="text-rose-500">*</span></Label><Input data-testid="supcreate-name" value={supForm.name || ""} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} className="h-9 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>GSTIN</Label><Input data-testid="supcreate-gstin" value={supForm.gstin || ""} onChange={(e) => setSupForm({ ...supForm, gstin: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>PAN</Label><Input data-testid="supcreate-pan" value={supForm.pan || ""} onChange={(e) => setSupForm({ ...supForm, pan: e.target.value })} className="h-9 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Phone</Label><Input data-testid="supcreate-phone" value={supForm.phone || ""} onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>Email</Label><Input data-testid="supcreate-email" value={supForm.email || ""} onChange={(e) => setSupForm({ ...supForm, email: e.target.value })} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label>Address</Label><Input data-testid="supcreate-address" value={supForm.address || ""} onChange={(e) => setSupForm({ ...supForm, address: e.target.value })} className="h-9 text-sm" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>City</Label><Input value={supForm.city || ""} onChange={(e) => setSupForm({ ...supForm, city: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>State</Label><Input value={supForm.state || ""} onChange={(e) => setSupForm({ ...supForm, state: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>PIN</Label><Input value={supForm.pin || ""} onChange={(e) => setSupForm({ ...supForm, pin: e.target.value })} className="h-9 text-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSupDlg(false); setSupDup(null); }}>Cancel</Button>
            <Button data-testid="supcreate-save" className="bg-amber-700 hover:bg-amber-800" disabled={!supForm.name?.trim() || busy} onClick={createSupplier}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : supDup ? "Create anyway" : "Create Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create ingredient dialog */}
      <Dialog open={ingDlg != null} onOpenChange={(o) => { if (!o) { setIngDlg(null); setIngDup(null); } }}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add New Ingredient</DialogTitle>
            <DialogDescription className="text-xs">From the invoice line. Source: Historical Master Data Import. Rate starts at ₹0 (price history is recorded separately). No stock created.</DialogDescription></DialogHeader>
          {ingDup && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="ingcreate-dup">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Possible duplicate: <strong>{ingDup.name}</strong> ({ingDup.ingredientId}). Click “Create anyway” to proceed, or Cancel and select the existing ingredient.
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1"><Label>Ingredient name <span className="text-rose-500">*</span></Label><Input data-testid="ingcreate-name" value={ingForm.name || ""} onChange={(e) => setIngForm({ ...ingForm, name: e.target.value })} className="h-9 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Brand</Label><Input data-testid="ingcreate-brand" value={ingForm.brand || ""} onChange={(e) => setIngForm({ ...ingForm, brand: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>Standard Unit</Label><Input data-testid="ingcreate-unit" value={ingForm.unit || ""} onChange={(e) => setIngForm({ ...ingForm, unit: e.target.value })} className="h-9 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Packing Size</Label><Input data-testid="ingcreate-packsize" type="number" value={ingForm.packingSize || ""} onChange={(e) => setIngForm({ ...ingForm, packingSize: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>Packing Unit</Label><Input data-testid="ingcreate-packunit" value={ingForm.packingUnit || ""} onChange={(e) => setIngForm({ ...ingForm, packingUnit: e.target.value })} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label>Category</Label><Input data-testid="ingcreate-category" value={ingForm.category || ""} onChange={(e) => setIngForm({ ...ingForm, category: e.target.value })} className="h-9 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIngDlg(null); setIngDup(null); }}>Cancel</Button>
            <Button data-testid="ingcreate-save" className="bg-amber-700 hover:bg-amber-800" disabled={!ingForm.name?.trim() || busy} onClick={createIngredient}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : ingDup ? "Create anyway" : "Create Ingredient"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import confirm */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Import master data &amp; price history</AlertDialogTitle>
            <AlertDialogDescription>Links the supplier &amp; matched ingredients and records the bill's historical prices. Source: <strong>Historical Master Data Import</strong>. It does <strong>NOT</strong> create a GRN, receive stock, or post any inventory / accounts-payable / purchase transaction. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supbill-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="supbill-confirm-save" className="bg-emerald-600 hover:bg-emerald-700" onClick={importMasterData}>Yes, import (no stock)</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receive to stock confirm */}
      <AlertDialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Boxes className="h-5 w-5 text-emerald-600" /> Receive billed quantities into stock</AlertDialogTitle>
            <AlertDialogDescription>
              This links the supplier &amp; matched ingredients, records prices, and <strong>adds the billed quantities of the {matchedCount} matched line(s) directly into raw-material stock</strong> (ledger-backed). This <strong>skips the GRN / QA step</strong>. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supbill-receive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="supbill-receive-save" className="bg-emerald-600 hover:bg-emerald-700" onClick={receiveToStock}>Yes, receive to stock</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
